import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import useUser from '@/hooks/useUser';
import { getErrorStatus } from '@/utils/sentryLogger';
import { useLoginQuery } from '@viome/viome-base/src/usecase/Login/LoginQuery';
import { LoginFormValues } from '@viome/viome-base/src/usecase/Login/Login.model';

const defaultSchema = z.object({
  email: z.string().trim().email(),
  password: z.string(),
  rememberMe: z.boolean().default(false),
});

// Only a 401 means bad credentials; anything else (auth service failure,
// upstream outage, network drop/timeout = -1) is a system error.
export type LoginError = 'invalidCredentials' | 'systemError';

const useLogin = () => {
  const [loginError, setLoginError] = useState<LoginError | null>(null);
  const { onLoginSuccess } = useUser();

  const { mutate, isPending } = useLoginQuery(onLoginSuccess);

  const form = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
    resolver: zodResolver(defaultSchema),
  });

  const onValid = (data: LoginFormValues) => {
    setLoginError(null);
    mutate(data, {
      onError: (error: Error) => {
        console.error('Login failed:', error.message);
        setLoginError(
          getErrorStatus(error) === 401 ? 'invalidCredentials' : 'systemError',
        );
      },
    });
  };

  return { onValid, form, loginError, isLoading: isPending };
};

export default useLogin;
